type Point = { x: number; y: number };
type Size = { width: number; height: number };
type Flip = { horizontal: boolean; vertical: boolean };

/**
 * Loads an image from a URL and returns it as an HTMLImageElement.
 * @param url - The URL of the image to load.
 * @returns A promise that resolves with the loaded image element.
 */
export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // Avoid cross-origin issues
    image.src = url;
  });

/**
 * Converts a degree value to radians.
 * @param degreeValue - The degree value to convert.
 * @returns The equivalent value in radians.
 */
export const getRadianAngle = (degreeValue: number): number =>
  (degreeValue * Math.PI) / 180;

/**
 * Calculates the bounding size of a rotated rectangle.
 * @param width - The original width of the rectangle.
 * @param height - The original height of the rectangle.
 * @param rotation - The rotation angle in degrees.
 * @returns The new width and height of the bounding box.
 */
export const rotateSize = (
  width: number,
  height: number,
  rotation: number,
): Size => {
  const rotRad = getRadianAngle(rotation);
  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
};

/**
 * Crops an image to the specified area and returns the cropped image as a Blob URL.
 * @param imageSrc - The source URL of the image to crop.
 * @param pixelCrop - The cropping area in pixels.
 * @param rotation - The rotation angle in degrees (default is 0).
 * @param flip - Whether to flip the image horizontally or vertically (default is no flip).
 * @returns A promise that resolves with the cropped image Blob URL.
 */
export const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Size & Point,
  rotation = 0,
  flip: Flip = { horizontal: false, vertical: false },
): Promise<Blob | null> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation,
  );

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");
  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    return null;
  }

  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise((resolve, reject) => {
    croppedCanvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Failed to create Blob"));
      }
    }, "image/png");
  });
};

/**
 * Rotates an image and returns it as a Blob URL.
 * @param imageSrc - The source URL of the image to rotate.
 * @param rotation - The rotation angle in degrees (default is 0).
 * @returns A promise that resolves with the rotated image Blob URL.
 */
export const getRotatedImage = async (
  imageSrc: string,
  rotation = 0,
): Promise<string> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }

  const orientationChanged =
    rotation === 90 ||
    rotation === -90 ||
    rotation === 270 ||
    rotation === -270;

  if (orientationChanged) {
    canvas.width = image.height;
    canvas.height = image.width;
  } else {
    canvas.width = image.width;
    canvas.height = image.height;
  }

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(URL.createObjectURL(blob));
      } else {
        reject(new Error("Failed to create Blob"));
      }
    }, "image/png");
  });
};
